import { inject, Injectable, Injector, NgZone } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { catchError, firstValueFrom, forkJoin, map, Observable, of, shareReplay } from 'rxjs';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { ObjectConfigMappingService } from 'src/app/Common/ObjectConfigMapping/ObjectConfigMapping.service';
import { ValueListService } from 'src/app/Common/ValueListService/value-list.service';
import { ValueListItemDTO } from 'src/app/Common/ValueListService/ValueListItem.interface';
import { DocumentEmailPreviewComponent } from './document-email-preview.component';
import { findDocEmailProfile } from './document-email-profiles';
import { DocEmailProfile, DocEmailSettings } from './document-email.types';

/**
 * Optional per tenant + branch settings: value list DOCEMAIL, one item per document type.
 *   VliCode   = Print page MasterType (e.g. SaleQuotation)
 *   AddField1 = status set on send (e.g. SNT); empty = do not change the status
 *   AddField2 = statuses the email can be sent from, comma separated (e.g. DFT,DRAFT); empty = any
 *   VliDesc   = note shown at the bottom of the dialog; empty = generated
 * Without an item the document type's defaults are used (see document-email-profiles.ts).
 */
export const DOC_EMAIL_SETTINGS_VLCODE = 'DOCEMAIL';

/**
 * Email preview before sending, for any document type in DOC_EMAIL_PROFILES.
 * Switched on per tenant + branch by the profile's ObjectConfig code.
 */
@Injectable({
  providedIn: 'root'
})
export class DocumentEmailPreviewService {
  private modalService = inject(NgbModal);
  private ngZone = inject(NgZone);
  private injector = inject(Injector);
  private toastr = inject(ToastrService);
  private userService = inject(UserLoginService);
  private ocmService = inject(ObjectConfigMappingService);
  private valueListService = inject(ValueListService);

  private cache = new Map<string, Observable<DocEmailSettings | null>>();

  /** Settings for this MasterType, or null when the preview is not enabled for the tenant + branch. */
  getSettings(masterType: string | null | undefined): Observable<DocEmailSettings | null> {
    const profile = findDocEmailProfile(masterType);
    const user = this.userService.User();
    if (!profile || !user?.companyId) {
      return of(null);
    }
    const key = `${user.companyId}|${user.loggedInBranchId}|${profile.masterType}`;
    let settings$ = this.cache.get(key);
    if (!settings$) {
      const scope = { tenantId: user.companyId, tenantBranchId: user.loggedInBranchId };
      settings$ = forkJoin({
        enabled: this.ocmService.ObjectConfigMapping_readByMenuCode({ menuCode: profile.menuCode, ...scope }).pipe(
          map(res => (res?.list || []).some(item =>
            (item.objectCode || '').trim().toUpperCase() === profile.objectCode.toUpperCase() && item.hasAccess === 1)),
          catchError(() => of(false))),
        items: this.valueListService.ValueListItem_readByVlCode({ vlCode: DOC_EMAIL_SETTINGS_VLCODE, ...scope }).pipe(
          map(res => res?.items || []),
          catchError(() => of([] as ValueListItemDTO[])))
      }).pipe(
        map(({ enabled, items }) => enabled ? this.buildSettings(profile, items) : null),
        shareReplay(1)
      );
      this.cache.set(key, settings$);
    }
    return settings$;
  }

  isEnabled(masterType: string | null | undefined): Observable<boolean> {
    return this.getSettings(masterType).pipe(map(s => !!s));
  }

  /** Opens the preview. Resolves true when the email request was saved. */
  async open(masterType: string, masterId: number): Promise<boolean> {
    const settings = await firstValueFrom(this.getSettings(masterType));
    if (!settings) {
      this.toastr.warning('Email preview is not enabled for this document in this branch.');
      return false;
    }
    // Check the status first: a document that cannot be sent gets a message, not a locked dialog.
    const doc = await firstValueFrom(settings.profile.load(this.injector, Number(masterId) || 0).pipe(catchError(() => of(null))));
    const status = (doc?.status || '').trim().toUpperCase();
    if (doc && settings.allowedStatuses.length && !settings.allowedStatuses.includes(status)) {
      const labels = settings.profile.statusLabels;
      const allowed = Array.from(new Set(settings.allowedStatuses.map(s => labels[s] || s))).join(', ');
      this.toastr.info(`${settings.profile.label} ${doc.number} is ${labels[status] || doc.status || 'not in a sendable status'}. `
        + `The email can be sent only from: ${allowed}.`);
      return false;
    }
    const ref = this.modalService.open(DocumentEmailPreviewComponent, {
      size: 'xl',
      backdrop: 'static',
      keyboard: false
    });
    ref.componentInstance.settings = settings;
    ref.componentInstance.masterId = Number(masterId) || 0;
    return ref.result.then(result => result === 'sent', () => false);
  }

  /** For custom HTML pages: window.ERP.openDocumentEmailPreview('SaleQuotation', id). */
  registerWindowBridge(): void {
    const w = window as any;
    w.ERP = w.ERP || {};
    // Custom page scripts run outside Angular's zone; open the modal inside it so it renders.
    w.ERP.openDocumentEmailPreview = (masterType: string, masterId: number) =>
      this.ngZone.run(() => this.open(masterType, masterId));
  }

  private buildSettings(profile: DocEmailProfile, items: ValueListItemDTO[]): DocEmailSettings {
    const item = items.find(i =>
      (i.vliCode || '').trim().toLowerCase() === profile.masterType.toLowerCase() && i.isActive !== 0 && !i.isDeleted);
    const codes = (text?: string) => (text || '').split(/[;,]/).map(s => s.trim().toUpperCase()).filter(Boolean);
    return {
      profile,
      allowedStatuses: item ? codes(item.addField2) : profile.defaults.allowedStatuses,
      statusOnSend: item ? (item.addField1 || '').trim().toUpperCase() : profile.defaults.statusOnSend,
      note: (item?.vliDesc || '').trim()
    };
  }
}
